import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { SlidePanel } from "@/components/ui/slide-panel";
import { ExtendedProject } from "@/types/project";
import { Stage } from "@/types/pipeline";

// ============================================================================
// Types
// ============================================================================

interface ProjectStagePanelProps {
  isOpen: boolean;
  onClose: () => void;
  project: ExtendedProject | null;
  stages: Stage[];
  onStageChange: (stageId: number) => Promise<void>;
  onChangePipeline?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function ProjectStagePanel({
  isOpen,
  onClose,
  project,
  stages,
  onStageChange,
  onChangePipeline,
}: ProjectStagePanelProps) {
  const [stageSearch, setStageSearch] = useState("");

  const handleClose = () => {
    setStageSearch("");
    onClose();
  };

  const handleStageSelect = async (stageId: number) => {
    await onStageChange(stageId);
    handleClose();
  };

  const filteredStages = stages.filter((stage) =>
    stage.name.toLowerCase().includes(stageSearch.toLowerCase())
  );

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Change Project Stage"
      width="md"
      position="right"
      showBackdrop={true}
      closeOnBackdropClick={true}
      topOffset="calc(4rem + 10px)"
      panelClassName="!z-[220] border-l-2 border-t-2 border-border rounded-tl-lg"
      backdropClassName="!left-0 z-[100]"
      immediateBackdrop={true}
      showCloseButton={true}
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto">
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search stages..."
              className="w-full border rounded px-3 py-2 mb-3"
              value={stageSearch}
              onChange={(e) => setStageSearch(e.target.value)}
              autoFocus
            />
            <div className="space-y-1">
              {filteredStages.map((stage) => (
                <div
                  key={stage.id}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors hover:bg-muted ${
                    stage.id === project?.current_stage_id
                      ? "bg-primary/10 border border-primary/20"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStageSelect(stage.id);
                  }}
                >
                  <div>
                    <span className="font-medium">{stage.name}</span>
                  </div>
                  {stage.id === project?.current_stage_id && (
                    <span className="text-xs text-primary font-semibold">
                      CURRENT
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          {onChangePipeline && (
            <div className="border-t pt-4 mt-4">
              <button
                className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-muted rounded-lg transition-colors"
                onClick={() => {
                  handleClose();
                  onChangePipeline();
                }}
              >
                <span className="flex items-center">
                  <ChevronRight className="h-4 w-4 mr-2" />
                  Change Pipeline
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </SlidePanel>
  );
}

ProjectStagePanel.displayName = "ProjectStagePanel";
